import { FC, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Page } from '@/components/Page.tsx';
import { Link } from '@/components/Link/Link.tsx';
import {
  TabsList,
  Section,
  Cell,
  Button,
  Caption,
  Headline,
  Subheadline,
  Spinner,
  Avatar,
  Textarea, Modal, Blockquote,
} from '@telegram-apps/telegram-ui';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSecretSanta } from '@/providers/SecretSantaProvider.tsx';
import { useAuthUser } from '@/providers';
import { useNavigation } from '@/providers/NavigationProvider.tsx';
import {TbUser, TbSnowflake, TbCalendar, TbCoin, TbSend, TbEdit, TbShare3, TbPlayerPlay, TbDoorExit, TbChevronRight} from 'react-icons/tb';
import {getGameMessages, sendGameMessage, startDraw, leaveSecretSantaGame, kickParticipant} from '@/api/apiMethods.ts';
import {SecretSantaMessage, SecretSantaParticipant, SecretSantaConversation} from '@/api/types.ts';
import {
  ModalHeader
} from "@telegram-apps/telegram-ui/dist/components/Overlays/Modal/components/ModalHeader/ModalHeader";
import {Icon28Close} from "@telegram-apps/telegram-ui/dist/icons/28/close";
import {AiOutlineUserDelete} from "react-icons/ai";
import {LuList} from "react-icons/lu";
import {renderWithHtml} from "@/helpers/renderWithHtml.tsx";
import {miniApp, setMiniAppHeaderColor, useSignal, disableVerticalSwipes, enableVerticalSwipes, isVerticalSwipesEnabled} from "@telegram-apps/sdk-react";
import {haptic} from "@/helpers/haptic.ts";
import {getUserAcronym} from "@/helpers/getUserAcronym.ts";

const EMPTY_MESSAGES: SecretSantaMessage[] = [];

export const SecretSantaGamePage: FC = () => {
  const { game, isLoading, refreshGame } = useSecretSanta();
  const { authUser } = useAuthUser();
  const { t, i18n: { language } } = useTranslation();
  const { navigateToRoute, showSnackbar } = useNavigation();
  const [messagesByChat, setMessagesByChat] = useState<Record<string, SecretSantaMessage[]>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loadingChatKey, setLoadingChatKey] = useState<string | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: SecretSantaConversation = searchParams.get('tab') === 'santa' ? 'santa' : 'recipient';
  const setActiveTab = (tab: SecretSantaConversation) => {
    if (tab === activeTab) return;
    haptic.selection();
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      if (tab === 'santa') next.set('tab', 'santa');
      else next.delete('tab');
      return next;
    }, { replace: true });
  };
  const [selectedParticipant, setSelectedParticipant] = useState<SecretSantaParticipant|null>(null)
  const isDark = useSignal(miniApp.isDark);
  const chatKey = `${game?.id}:${activeTab}`;
  const messages = messagesByChat[chatKey] || EMPTY_MESSAGES;
  const lastMessageID = messages[messages.length - 1]?.id;
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageComposerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const messageText = drafts[chatKey] || '';
  const setMessageText = (text: string) => setDrafts(current => ({ ...current, [chatKey]: text }));
  const isMessagesLoading = loadingChatKey === chatKey;

  const mergeMessages = (key: string, incoming: SecretSantaMessage[]) => {
    setMessagesByChat(current => {
      const byID = new Map((current[key] || []).map(message => [message.id, message]));
      incoming.forEach(message => byID.set(message.id, message));
      return { ...current, [key]: [...byID.values()].sort((a, b) => a.sent_at.getTime() - b.sent_at.getTime()) };
    });
  };

  useEffect(() => {
    const wasEnabled = isVerticalSwipesEnabled();
    disableVerticalSwipes.ifAvailable();
    return () => {
      if (wasEnabled) enableVerticalSwipes.ifAvailable();
    };
  }, []);

  useEffect(() => {
    if (!setMiniAppHeaderColor.supports.rgb()) return;

    const setHeaderColor = (property: string) => {
      const color = getComputedStyle(document.documentElement)
        .getPropertyValue(property).trim();
      setMiniAppHeaderColor(color as Parameters<typeof setMiniAppHeaderColor>[0]);
    };

    setHeaderColor('--tg-theme-section-bg-color');
    return () => {
      setHeaderColor('--tg-theme-secondary-bg-color');
    };
  }, [isDark]);

  useLayoutEffect(() => {
    const textarea = messageComposerRef.current?.querySelector('textarea');
    if (!textarea) return;
    textarea.style.height = '48px';
    textarea.style.overflowY = 'hidden';
    const contentHeight = textarea.scrollHeight;
    textarea.style.height = `${Math.min(144, Math.max(48, contentHeight))}px`;
    textarea.style.overflowY = contentHeight > 144 ? 'auto' : 'hidden';
  }, [messageText, chatKey, isLoading]);

  useLayoutEffect(() => {
    stickToBottomRef.current = true;
  }, [chatKey]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (container && lastMessageID && stickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [chatKey, lastMessageID]);


  useEffect(() => {
    if (!game || game.status === 'open' || !game.participants.some(p => p.user_id === authUser?.id)) return;
    let cancelled = false;
    let inFlight = false;
    const controller = new AbortController();
    setLoadingChatKey(chatKey);

    const fetchMessages = async (initial = false) => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const fetchedMessages = await getGameMessages(game.id, activeTab, controller.signal);
        if (!cancelled) mergeMessages(chatKey, fetchedMessages);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load messages:', error);
          if (initial) showSnackbar('❌', 'secretSanta.error.messageFailed');
        }
      } finally {
        inFlight = false;
        if (!cancelled && initial) setLoadingChatKey(current => current === chatKey ? null : current);
      }
    };

    void fetchMessages(true);
    const interval = window.setInterval(() => { void fetchMessages(); }, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      controller.abort();
    };
  }, [game?.id, game?.status, authUser?.id, activeTab]);

  const handleSendMessage = async () => {
    if (!game || !messageText.trim() || isSendingMessage) return;
    haptic.impact('light');
    const sendingKey = chatKey;
    const sendingText = messageText;
    const sendingTab = activeTab;
    setIsSendingMessage(true);
    try {
      const sentMessage = await sendGameMessage(game.id, sendingTab, sendingText);
      mergeMessages(sendingKey, [sentMessage]);
      setDrafts(current => current[sendingKey] === sendingText
        ? { ...current, [sendingKey]: '' }
        : current);
      haptic.notification('success');
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.messageFailed');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleStartDraw = async () => {
    if (!game) return;
    
    if (game.participants.length < 3) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.insufficientParticipants');
      return;
    }

    haptic.notification('warning');
    const confirmed = confirm(t('secretSanta.dialog.drawConfirm'));
    if (!confirmed) return;

    setIsDrawing(true);
    try {
      await startDraw(game.id);
      haptic.notification('success');
      showSnackbar('✅', 'secretSanta.status.drawn');
      window.location.reload();
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.drawFailed');
    } finally {
      setIsDrawing(false);
    }
  };

  const handleShareGame = async () => {
    if (!game) return;
    haptic.impact('light');
    const gameLink = `${import.meta.env.VITE_MINIAPP_URL}?startapp=-s${game.short_id}`;
    try {
      const { shareLink } = await import('@/helpers/shareLink');
      await shareLink(gameLink, t('secretSanta.share.shareText'));
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'system.unknowError');
    }
  };

  const handleLeaveGame = async () => {
    if (!game || game.status !== 'open') return;
    
    haptic.notification('warning');
    const confirmed = confirm(t('secretSanta.dialog.leaveConfirm'));
    if (!confirmed) return;

    try {
      await leaveSecretSantaGame(game.id);
      haptic.notification('success');
      showSnackbar('✅', 'secretSanta.participants.leave');
      navigateToRoute('/main/secret-santa', true);
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.leaveFailed');
    }
  };

  if (isLoading) {
    return (
      <Page>
        <div className="flex justify-center items-center p-10">
          <Spinner size="l" />
        </div>
      </Page>
    );
  }

  if (!game) {
    return (
      <Page>
        <div className="p-10 text-center">
          <Caption className="text-color-hint">{t('secretSanta.error.loadFailed')}</Caption>
        </div>
      </Page>
    );
  }

  const isOrganizer = game.organizer_id === authUser?.id;
  const myParticipant = game.participants.find(p => p.user_id === authUser?.id);
  const isParticipant = !!myParticipant;
  const isDrawn = game.status !== 'open';
  const receiver = game.participants.find(p => p.user_id === myParticipant?.receiver_id);

  const formatDate = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusText = (status: string) => {
    return t(`secretSanta.status.${status}`);
  };

  const bottomSheetOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedParticipant(null)
    }
  }

  const canOpenParticipant = (participant: SecretSantaParticipant) =>
    participant.user_id === authUser?.id || (isDrawn && participant.user_id === myParticipant?.receiver_id);

  const openParticipantModal = (participant: SecretSantaParticipant) => {
    if (canOpenParticipant(participant)) {
      haptic.impact('soft');
      setSelectedParticipant(participant);
    }
  }

  const kickSelectedParticipant = async () => {
    if (!game || !selectedParticipant || !isOrganizer || game.status !== 'open') return;

    haptic.notification('warning');
    const confirmed = confirm(t('secretSanta.dialog.kickConfirm'));
    if (!confirmed) return;

    try {
      await kickParticipant(game.id, selectedParticipant.user_id);
      haptic.notification('success');
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'system.unknowError');
    } finally {
      setSelectedParticipant(null);
      await refreshGame();
    }
  }

  const participantsTitle = t('secretSanta.participants.title') + ` (${game.participants.length})`;
  const participantsContent = (
    <>
      {myParticipant ? <Cell
        onClick={() => {openParticipantModal(myParticipant)}}
        key={myParticipant.user_id}
        before={<Avatar size={40} acronym={getUserAcronym(myParticipant.display_name)} />}
        className={"pb-3 pt-3"}
      >
        {myParticipant.display_name}
        <Caption className="tg-text-button-color"> ({t(isDrawn ? 'secretSanta.participants.you' : 'secretSanta.participants.youParticipate')})</Caption>
      </Cell> : null}
      {game.participants.map((participant) => {
        return myParticipant?.user_id !== participant.user_id && <Cell
          onClick={canOpenParticipant(participant) ? () => openParticipantModal(participant) : undefined}
          readOnly={!canOpenParticipant(participant)}
          key={participant.user_id}
          before={<Avatar size={40} acronym={getUserAcronym(participant.display_name)}/>}
          description={isDrawn && participant.user_id === myParticipant?.receiver_id ? (
            <Caption className="tg-text-button-color">{t('secretSanta.participants.yourRecipient')}</Caption>
          ) : undefined}
          className={"pb-3 pt-3"}
        >
          {participant.display_name}
        </Cell>
      })}
      {isOrganizer && game.status === 'open' && (
        <div className="p-3">
          <Button
            stretched={true}
            onClick={handleStartDraw}
            before={<TbPlayerPlay size={20} />}
            mode="filled"
            disabled={isDrawing}
            loading={isDrawing}
          >
            {t('secretSanta.actions.startDraw')}
          </Button>
        </div>
      )}
    </>
  );

  const sheetCloseButton = (
    <Modal.Close>
      <button
        type="button"
        aria-label={t('action.cancel')}
        onClick={() => haptic.impact('light')}
        style={{ background: 'none', border: 0, padding: 0, display: 'flex', cursor: 'pointer' }}
      >
        <Icon28Close style={{ color: 'var(--tgui--plain_foreground)' }} />
      </button>
    </Modal.Close>
  );

  const renderParticipantSheet = (nested: boolean) => (
    <Modal
      nested={nested}
      header={<ModalHeader after={sheetCloseButton}>{selectedParticipant?.display_name}</ModalHeader>}
      open={selectedParticipant !== null}
      onOpenChange={bottomSheetOpenChange}
      className="p-3"
      style={{
        backgroundColor: isDark ? 'var(--tgui--section_bg_color)' : 'var(--tgui--secondary_bg_color)',
        maxHeight: 'calc(100% - 50px)',
      }}
    >
            <Blockquote type="text" className={"description"}>
                  {selectedParticipant?.preferences ?
                    renderWithHtml(selectedParticipant?.preferences) :
                    "("+t('secretSanta.assignment.noPreferences')+")"}
            </Blockquote>

            {selectedParticipant?.wishlist_id && (
              <Button
                stretched={true}
                onClick={() => {
                  haptic.impact('light');
                  navigateToRoute(`/main/wishlists/${selectedParticipant?.wishlist_id}${selectedParticipant?.user_id === receiver?.user_id ? `?secret_santa_game_id=${encodeURIComponent(game.id)}` : ''}`);
                }}
                mode="filled"
                before={<LuList size={20} />}
                className="mt-4 mb-2"
              >
                {t('secretSanta.assignment.viewWishlist')}
              </Button>
            )}
            <div className={"mt-10"}>
              {selectedParticipant?.user_id === authUser?.id && <div>
                <Button
                  stretched={true}
                  className="mb-2"
                  onClick={() => {
                    haptic.impact('light');
                    navigateToRoute(`/main/secret-santa/${game.id}/join`);
                  }}
                  before={<TbEdit size={20} />}
                  mode="gray"
                >
                  {t('secretSanta.participants.edit')}
                </Button>

                {game.status === 'open' && <Button
                  stretched={true}
                  className="mb-1"
                  onClick={handleLeaveGame}
                  before={<TbDoorExit size={20} />}
                  mode="plain"
                >
                  {t('secretSanta.participants.leave')}
                </Button>}
              </div>}
              {isOrganizer && game.status === 'open' && selectedParticipant && selectedParticipant.user_id !== authUser?.id && <Button
                  stretched={true}
                  className="mb-2 text-color-destroy"
                  onClick={kickSelectedParticipant}
                  before={<AiOutlineUserDelete size={20} />}
                  mode="plain"
                >
                  {t('secretSanta.participants.kickParticipant')}
              </Button>}
            </div>
    </Modal>
  );

  return (
    <Page>
      <div className="flex flex-col gap-3 pb-10">
        {/* Game Header */}
        <div className="p-4 bg-section rounded-b-xl flex flex-col gap-3">
          <Caption className="self-start">
            <Link to="/main" onClick={() => haptic.impact('light')}>
              {t('navigation.toMain')}
            </Link>
          </Caption>
          <Headline weight="2" className="flex items-center gap-2">
            <TbSnowflake size={24} aria-hidden="true" />
            {t('secretSanta.title')}
          </Headline>
          {/*{game.cover_image_id && (*/}
          {/*  <div className="w-full h-32 bg-gray-200 rounded-lg"></div>*/}
          {/*)}*/}
          <Headline weight="2">
            {game.title}
          </Headline>
          <Caption>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
              {getStatusText(game.status)}
            </span>
          </Caption>
          {game.description && (
            <Subheadline level="2" className="text-color-hint">
              {game.description}
            </Subheadline>
          )}
          <div className="flex flex-col gap-2">
            <Caption
              Component={isDrawn ? 'button' : 'span'}
              type={isDrawn ? 'button' : undefined}
              className={`flex items-center gap-2 self-start ${isDrawn ? 'tg-link-color' : ''}`}
              style={{ background: 'none', border: 0, padding: 0, cursor: isDrawn ? 'pointer' : 'default' }}
              onClick={isDrawn ? () => {
                haptic.impact('soft');
                setSelectedParticipant(null);
                setIsParticipantsOpen(true);
              } : undefined}
            >
              <TbUser size={16} aria-hidden="true" />
              {t('secretSanta.participants.total', { count: game.participants.length })}
            </Caption>
            {game.budget && (
              <Caption className="flex items-center gap-2">
                <TbCoin size={16} />
                {t('secretSanta.form.budget') + ": "}
                {game.budget.number} {game.budget.currency}
              </Caption>
            )}
            {game.event_date && (
              <Caption className="flex items-center gap-2">
                <TbCalendar size={16} />
                {t('secretSanta.form.eventDate') + ": "}
                {formatDate(game.event_date)}
              </Caption>
            )}
          </div>
          {/* Organizer Actions */}
          {isOrganizer && (
            game.status === 'open' && (
              <div>
                <Button
                  stretched={true}
                  onClick={() => {
                    haptic.impact('light');
                    navigateToRoute(`/main/secret-santa/${game.id}/edit`);
                  }}
                  before={<TbEdit size={20} />}
                  mode="plain"
                >
                  {t('secretSanta.actions.edit')}
                </Button>
              </div>
            )
          )}
          <Button
            stretched={true}
            onClick={handleShareGame}
            after={<TbShare3 size={20} />}
            mode="plain"
          >
            {t('secretSanta.actions.share')}
          </Button>
          {!isParticipant && game.status === 'open' && (
            <Button
              stretched={true}
              onClick={() => {
                haptic.impact('light');
                navigateToRoute(`/main/secret-santa/${game.id}/join`);
              }}
              mode="filled"
              after={<TbChevronRight size={20}/>}
            >
              {t('secretSanta.participants.join')}
            </Button>
          )}
        </div>

        {isParticipant && isDrawn && (
          <div className="flex flex-col gap-3">
            <TabsList>
              <TabsList.Item selected={activeTab === 'recipient'} onClick={() => setActiveTab('recipient')}>
                <span className="inline-flex items-center justify-center gap-2">
                  <Avatar size={24} acronym={getUserAcronym(receiver?.display_name || '')} className="shrink-0" />
                  {t('secretSanta.tabs.recipient')}
                </span>
              </TabsList.Item>
              <TabsList.Item selected={activeTab === 'santa'} onClick={() => setActiveTab('santa')}>
                <span className="inline-flex items-center justify-center gap-2">
                  <Avatar size={24} src={`${import.meta.env.BASE_URL}secret-santa.webp`} className="shrink-0" />
                  {t('secretSanta.tabs.santa')}
                </span>
              </TabsList.Item>
            </TabsList>
            <Section>
              <Cell
                before={activeTab === 'recipient' && receiver
                  ? <Avatar size={40} acronym={getUserAcronym(receiver.display_name)} />
                  : <Avatar size={40} src={`${import.meta.env.BASE_URL}secret-santa.webp`} />}
                onClick={activeTab === 'recipient' && receiver ? () => openParticipantModal(receiver) : undefined}
                titleBadge={
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center">
                    {isMessagesLoading && <Spinner size="s" />}
                  </span>
                }
                className="pb-3 pt-3"
              >
                {activeTab === 'recipient' && receiver
                  ? <span className="tg-link-color">{receiver.display_name}</span>
                  : t('secretSanta.messages.yourSecretSanta')}
              </Cell>
                <div
                  ref={messagesContainerRef}
                  className="flex h-80 flex-col gap-2 overflow-y-auto p-3"
                  onScroll={(event) => {
                    const element = event.currentTarget;
                    stickToBottomRef.current = element.scrollHeight - element.scrollTop - element.clientHeight <= 2;
                  }}
                >
                  {activeTab === 'santa' && (
                    <Button
                      size="s"
                      mode="plain"
                      before={<TbEdit size={16} />}
                      className="self-start mb-2"
                      onClick={() => {
                        haptic.impact('light');
                        navigateToRoute(`/main/secret-santa/${game.id}/join?tab=santa`);
                      }}
                    >
                      {t('secretSanta.messages.editPreferences')}
                    </Button>
                  )}
                  {activeTab === 'recipient' && receiver && messages.length === 0 && (
                    <div className="flex flex-col gap-2 mb-2">
                      {receiver.wishlist_id && (
                        <Button
                          size="s"
                          before={<LuList size={16} />}
                          mode="plain"
                          className="self-start"
                          onClick={() => {
                            haptic.impact('light');
                            navigateToRoute(`/main/wishlists/${receiver.wishlist_id}?secret_santa_game_id=${encodeURIComponent(game.id)}`);
                          }}
                        >
                          {t('secretSanta.assignment.viewWishlist')}
                        </Button>
                      )}
                      {receiver.preferences?.trim() && (
                        <Subheadline level="2" className="text-color-hint">
                          {renderWithHtml(receiver.preferences)}
                        </Subheadline>
                      )}
                    </div>
                  )}
                  {messages.length === 0 ? (
                    <Caption className="text-color-hint px-1 py-2">
                      {t(activeTab === 'recipient' ? 'secretSanta.messages.recipientEmptyState' : 'secretSanta.messages.emptyState')}
                    </Caption>
                  ) : (
                    messages.map((message) => {
                      const isFromMe = message.sender_id === authUser?.id;
                      return (
                        <div
                          key={message.id}
                          className={`flex items-start gap-2 ${isFromMe ? 'justify-end' : 'justify-start'}`}
                        >
                          {!isFromMe && (activeTab === 'recipient' ? (
                            <Avatar size={40} acronym={getUserAcronym(receiver?.display_name || '')} className="shrink-0" />
                          ) : (
                            <img
                              src={`${import.meta.env.BASE_URL}secret-santa.webp`}
                              alt={t('secretSanta.messages.secretSanta')}
                              width={40}
                              height={40}
                              className="h-10 w-10 shrink-0 object-contain"
                            />
                          ))}
                          <div className={`flex min-w-0 max-w-[80%] flex-col gap-1 ${isFromMe ? 'items-end' : 'items-start'}`}>
                            <div
                              className={`p-3 rounded-lg break-words max-w-full ${
                                isFromMe ? 'bg-blue-500 text-white' : 'bg-secondary'
                              }`}
                            >
                              <Caption className={`whitespace-pre-wrap ${isFromMe ? 'text-white' : ''}`}>
                                {renderWithHtml(message.content)}
                              </Caption>
                            </div>
                            <Caption className="text-color-hint text-xs">
                              {message.sent_at.toLocaleTimeString(language, {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Caption>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div ref={messageComposerRef} className="grid min-w-0 grid-cols-[minmax(0,1fr)_48px] items-end gap-2 p-3">
                  <Textarea
                    placeholder={t('secretSanta.messages.placeholder')}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    className="min-w-0 w-full"
                    style={{ boxSizing: 'border-box', minHeight: 48, height: 48, maxHeight: 144, padding: '12px 16px', overflowY: 'hidden' }}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || isSendingMessage}
                    loading={isSendingMessage}
                    aria-label={t('secretSanta.messages.send')}
                    title={t('secretSanta.messages.send')}
                    className="shrink-0"
                    style={{ width: 48, minWidth: 48, height: 48, padding: 0 }}
                  >
                    <span className="flex items-center justify-center">
                      <TbSend size={20} aria-hidden="true" />
                    </span>
                  </Button>
                </div>
            </Section>
          </div>
        )}

        {!isDrawn && (
          <Section header={participantsTitle}>
            {participantsContent}
          </Section>
        )}
      </div>

      <Modal
        header={<ModalHeader after={sheetCloseButton}>{participantsTitle}</ModalHeader>}
        open={isParticipantsOpen}
        onOpenChange={(open) => {
          setIsParticipantsOpen(open);
          if (!open) setSelectedParticipant(null);
        }}
        snapPoints={[1]}
        className="box-border p-3"
        style={{
          backgroundColor: isDark ? 'var(--tgui--section_bg_color)' : 'var(--tgui--secondary_bg_color)',
          height: '90dvh',
          maxHeight: '90dvh',
        }}
      >
        <div className="w-full">{participantsContent}</div>
        {renderParticipantSheet(true)}
      </Modal>
      {!isParticipantsOpen && renderParticipantSheet(false)}
    </Page>
  );
};
